import { defineStore } from 'pinia';
import getApartment from '../../lib/getApartment';

// Helper functions for localStorage
function getFavourites(type) {
  return JSON.parse(localStorage.getItem(`favourites-${type}`)) || [];
}

function storeFavourites(type, favourites) {
  localStorage.setItem(`favourites-${type}`, JSON.stringify(favourites));
}

const localStorageKey = (type) => `application[${type}]`;

function getLocalSave(type) {
  return JSON.parse(localStorage.getItem(localStorageKey(type))) || null;
}

// Main favourites store
export const useFavouritesStore = defineStore('favourites', {
  state: () => ({
    // Favourites as UUIDs. Loaded from localstorage on every page load. The source of truth.
    favourites: {
      'right-of-occupancy': getFavourites('right-of-occupancy'),
      rental: getFavourites('rental'),
      'owner-occupancy': getFavourites('owner-occupancy'),
    },
    // Favourites as apartment objects. Populated using requestAndCleanUpFavourites whenever needed.
    favouritesData: {
      'right-of-occupancy': [],
      'owner-occupancy': [],
      rental: [],
    },
    favouritesDataLoading: false,
    favouritesMenuOpenExternal: false,
    // Local save state (from localSave module)
    localSave: {
      'right-of-occupancy': getLocalSave('right-of-occupancy'),
      'owner-occupancy': getLocalSave('owner-occupancy'),
      rental: getLocalSave('rental'),
    },
  }),
  
  getters: {
    favouritesAll: (state) => {
      return Object.values(state.favourites).flat();
    },
    favouritesRental: (state) => {
      return Object.values(state.favourites.rental).flat();
    },
    favouritesASO: (state) => {
      return Object.values(state.favourites['right-of-occupancy']).flat();
    },
    favouritesDataAll: (state) => {
      return Object.values(state.favouritesData).flat();
    },
    isFavourite: (state) => (uuid) => {
      return Object.values(state.favourites).flat().includes(uuid);
    },
    getFavouritesMenuOpenExternal: (state) => {
      return state.favouritesMenuOpenExternal;
    },
    // LocalSave getters
    getLocalSave: (state) => (type) => {
      return state.localSave[type];
    },
  },

  actions: {
    emptyFavourites(args = {}) {
      const types = args.hasOwnProperty('type')
        ? [args.type]
        : Object.keys(this.favourites);
      types.forEach((type) => {
        this.favourites[type] = [];
        storeFavourites(type, this.favourites[type]);
      });
    },

    addFavourite({ type, uuid }) {
      if (this.favourites[type].includes(uuid)) {
        return;
      }
      this.favourites[type].push(uuid);
      storeFavourites(type, this.favourites[type]);
    },

    removeFavourite({ type, uuid }) {
      const index = this.favourites[type].indexOf(uuid);
      if (index === -1) {
        return;
      }
      this.favourites[type].splice(index, 1);
      storeFavourites(type, this.favourites[type]);
    },

    updateFavouritesData({ apartments }) {
      Object.keys(this.favouritesData).forEach((type) => {
        this.favouritesData[type] = this.favourites[type]
          .map((uuid) =>
            apartments.find((apartment) => apartment.mpc_asuntoid === uuid)
          )
          // Sort according to lexical order - cities first, then street name.
          .sort((apartment1, apartment2) =>
            apartment1.mpc_name > apartment2.mpc_name ? 1 : -1
          )
          .sort((apartment1, apartment2) => {
            if (apartment1.city.mpc_name === apartment2.city.mpc_name) return 0;
            return apartment1.city.mpc_name > apartment2.city.mpc_name ? 1 : -1;
          })
          .filter((apartment) => apartment);
      });
    },

    increaseFavouritesDataLoading() {
      this.favouritesDataLoading++;
    },

    decreaseFavouritesDataLoading() {
      this.favouritesDataLoading--;
    },

    toggleFavouritesMenuExternal(value) {
      this.favouritesMenuOpenExternal = value;
    },

    // Favourite apartments that are no longer publicly accessible must be removed from favourites when encountered.
    async requestAndCleanUpFavourites() {
      // A copy of favourites is created so that future changes won't mess with clean up logic later.
      const favouritesCopy = {
        'right-of-occupancy': [...this.favourites['right-of-occupancy']],
        'owner-occupancy': [...this.favourites['owner-occupancy']],
        rental: [...this.favourites['rental']],
      };

      const favourites = [
        ...favouritesCopy['right-of-occupancy'],
        ...favouritesCopy['owner-occupancy'],
        ...favouritesCopy['rental'],
      ];

      this.increaseFavouritesDataLoading();

      const apartments = await getApartment(favourites);

      // Clean up all missing apartments by comparing requested UUIDs (favourites) to resulted apartments.
      const cleanUpFavourites = (type) => {
        favouritesCopy[type].forEach((uuid) => {
          const found = apartments.find(
            (apartment) => apartment.mpc_asuntoid === uuid
          );
          if (found) {
            return;
          }
          this.removeFavourite({ uuid, type });
        });
      };

      cleanUpFavourites('right-of-occupancy');
      cleanUpFavourites('owner-occupancy');
      cleanUpFavourites('rental');

      this.updateFavouritesData({ apartments });
      this.decreaseFavouritesDataLoading();
    },

    // LocalSave actions
    storeLocalSave({ type, localSave }) {
      this.localSave[type] = localSave;
      localStorage.setItem(localStorageKey(type), JSON.stringify(this.localSave[type]));
    },

    removeLocalSave({ type }) {
      this.localSave[type] = null;
      localStorage.removeItem(localStorageKey(type));
    },
  },
});

// Export default for backwards compatibility during migration
// Components should migrate to using useFavouritesStore() directly
export default {
  // This allows legacy $store access to work during transition
  // Components should be updated to use useFavouritesStore() instead
};
